import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import { Title } from '@winnin/winnin_ui.components.title'

import LoadingErrorWrapper from 'components/LoadingErrorWrapper'
import SeeMoreCard from 'components/SeeMoreCard'
import type { CreateNichesBySearchMutation, TaxonomyLanguages } from 'generated/graphql'
import { useTranslate } from 'lib/tolgee'
import { datadog, TOLGEE_GLOBALS } from 'utils'

import { CREATE_NICHES_BY_SEARCH } from './query'
import { translations } from './translations'
import './index.scss'
import { CREATE_NICHES_BY_SEARCH_LOG } from '../../consts/queryLogs'
import TerritoryInSearch from '../TerritoryInSearch'

interface ITerritoriesInSearch {
  searchTerm: string
}

type ITerritoriesList = {
  language?: TaxonomyLanguages
  topicIn?: string
  parentName: string
  territoryId: string
  name: string
}

export default function TerritoriesInSearchList({ searchTerm }: ITerritoriesInSearch) {
  const [isSeeMore, setIsSeeMore] = useState(false)

  const t = useTranslate()

  const [createNichesBySearch, { data, loading, error }] =
    useMutation<CreateNichesBySearchMutation>(CREATE_NICHES_BY_SEARCH)

  const handleCreateNiches = useCallback(() => {
    const variables = {
      query: searchTerm,
      pagination: {
        take: 16,
      },
    }
    try {
      createNichesBySearch({
        variables,
      })
    } catch (error) {
      datadog.dispatchRequestErrorLog({
        log: CREATE_NICHES_BY_SEARCH_LOG,
        query: {
          name: 'CREATE_NICHES_BY_SEARCH',
          variables,
        },
        error,
      })
    }
  }, [createNichesBySearch, searchTerm])

  useEffect(() => {
    handleCreateNiches()
  }, [createNichesBySearch, handleCreateNiches, searchTerm])

  const territoriesList: ITerritoriesList[] = useMemo(() => {
    if (!data?.createNichesBySearch) {
      return []
    }

    const baseList = data?.createNichesBySearch?.data
    const formattedList = baseList
      .map(item => {
        if (item?.__typename !== 'Niche') {
          return null
        }
        if (item?.parent?.__typename === 'Topic') {
          return {
            ...item,
            language: item?.parent?.language as TaxonomyLanguages,
            topicIn: item?.parent?.category?.title,
            parentName: item?.parent?.name,
          }
        }
        if (item?.parent?.__typename === 'Subtopic') {
          return {
            ...item,
            language: item?.parent?.language as TaxonomyLanguages,
            topicIn: item?.parent?.topic?.name,
            parentName: item?.parent?.name,
          }
        }
        return null
      })
      .filter(item => item)

    if (isSeeMore) {
      return formattedList
    }

    return formattedList.slice(0, 8)
  }, [data?.createNichesBySearch, isSeeMore])

  const hasMore = useMemo(() => {
    if (!data?.createNichesBySearch) {
      return false
    }
    const isPastTotal = data?.createNichesBySearch?.totalCount > data?.createNichesBySearch?.data?.length
    return isPastTotal
  }, [data?.createNichesBySearch])

  return (
    <div className="TerritoriesInSearchList-wrapper">
      <Title tag="h2">{t(translations.title({ searchTerm }))}</Title>
      <LoadingErrorWrapper
        error={error}
        loading={loading}
        skeletonComponent={LoadingSkeleton}
        refetch={handleCreateNiches}
        isEmpty={!loading && !territoriesList.length}
      >
        <div className="listWrapper">
          {territoriesList?.map(item => {
            return (
              <TerritoryInSearch
                language={item?.language}
                topicIn={item?.topicIn}
                parentName={item?.parentName}
                search={item?.name}
                territoryId={item?.territoryId}
                key={item?.territoryId}
              />
            )
          })}
        </div>
      </LoadingErrorWrapper>
      {hasMore && (
        <SeeMoreCard
          className="seeMoreCard"
          text={!isSeeMore ? t(TOLGEE_GLOBALS.seeMore) : t(TOLGEE_GLOBALS.seeLess)}
          onClick={() => setIsSeeMore(prevState => !prevState)}
          disabled={loading}
        />
      )}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="listWrapper">
      <Skeleton height={108} borderRadius={20} baseColor="var(--color_neutral_400)" />
      <Skeleton height={108} borderRadius={20} baseColor="var(--color_neutral_400)" />
      <Skeleton height={108} borderRadius={20} baseColor="var(--color_neutral_400)" />
      <Skeleton height={108} borderRadius={20} baseColor="var(--color_neutral_400)" />
    </div>
  )
}
